import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useQuery } from "react-query";
import axios from "../../config/axiosInstance";
import Input from "../inputs/Input";
import PhoneInput from "../inputs/PhoneInput";
import { Alert, Button, Checkbox, TextInput } from "flowbite-react";
import { SURVEYS, USERS } from "../../config/apiConfig";
import { AiOutlineLoading } from "react-icons/ai";
import ImageInput from "../inputs/ImageInput";
import { Link } from "react-router-dom";

const SurveyConfig = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorSubmit, setErrorSubmit] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [avatarImg, setAvatarImg] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const [mandatoryTags, setMandatoryTags] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    register,
    getValues,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const {
    isLoading,
    error,
    data: configuration,
  } = useQuery(["configuration"], async () => {
    const { data: item } = await axios.get(`${SURVEYS.GET_CONFIG}`);
    return item.data;
  });

  const { data: tags, isLoading: loadingTags } = useQuery(
    ["surveyTags"],
    async () => {
      const { data } = await axios.get(SURVEYS.GET_TAGS);
      return data.data;
    }
  );

  useEffect(() => {
    if (configuration) {
      Object.keys(configuration).forEach((key) => {
        setValue(key, configuration[key]);
      });

      setPreviewUrl(configuration?.imgURL);
    }
  }, [configuration]);

  useEffect(() => {
    if (tags) {
      const mandatoryIds = tags.filter((t) => t.isMandatory).map((t) => t.id);
      setMandatoryTags(mandatoryIds);
    }
  }, [tags]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("image", avatarImg);
      formData.append("config", JSON.stringify(data));
      formData.append("mandatoryTags", JSON.stringify(mandatoryTags));

      const response = await axios.put(SURVEYS.GET_CONFIG, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setAlert({
        show: true,
        message: "Informações atualizadas com sucesso!",
        type: "success",
      });
    } catch (error) {
      setErrorSubmit(error);
      setAlert({
        show: true,
        message: "Erro ao tentar atualizar informações!",
        type: "failure",
      });
    } finally {
      setIsSubmitting(false);
      setTimeout(() => {
        setAlert({
          show: false,
          message: "",
          type: "",
        });
      }, 2000);
    }
  };

  return (
    <div className="bg-white px-4 py-4 rounded-md shadow-md">
      <Link
        to="/surveys"
        className="bg-gray-500 py-1 px-5 w-fit inline-block mb-5 text-sm text-white rounded-md"
      >
        Voltar
      </Link>
      <form className="mb-10" onSubmit={handleSubmit(onSubmit)}>
        <div className="title-syncx mb-6">Configurações do laudo</div>
        <div className="mb-6">
          <ImageInput
            image={avatarImg}
            setImage={setAvatarImg}
            previewUrl={previewUrl}
            setPreviewUrl={setPreviewUrl}
            description="PNG da logo com fundo transparente"
          />
        </div>
        <div className="mb-6">
          <PhoneInput
            type="text"
            label="Telefone"
            name="phoneNumber"
            register={register}
          />
        </div>
        <div className="mb-6">
          <Input
            type="text"
            label="URL do site"
            name="websiteURL"
            required={false}
            register={register}
          />
        </div>

        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="w-full relative flex-1">
              <select
                id="colorSurvey"
                name="colorSurvey"
                className="block px-2.5 pb-2.5 pt-4 w-full text-sm text-gray-900 bg-transparent rounded-lg border-1 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-green-500 focus:outline-none focus:ring-0 focus:border-green-600 peer"
                {...register("colorSurvey")}
              >
                <option value="verde">Verde</option>
                <option value="azul">Azul</option>
                <option value="vermelho">Vermelho</option>
                <option value="roxo">Roxo</option>
                <option value="laranja">Laranja</option>
              </select>
              <label
                htmlFor="colorSurvey"
                className="absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-4 scale-75 top-2 z-10 origin-[0] bg-white dark:bg-gray-900 px-2 peer-focus:px-2 peer-focus:text-green-600 peer-focus:dark:text-green-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:-translate-y-1/2 peer-placeholder-shown:top-1/2 peer-focus:top-1.5 peer-focus:scale-75 peer-focus:-translate-y-4 rtl:peer-focus:translate-x-1/4 rtl:peer-focus:left-auto start-1"
              >
                Cor do laudo
              </label>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <div className="title-syncx mb-3">Tags de imagens obrigatórias</div>
          <div className="mb-4">
            <TextInput
              type="text"
              placeholder="Buscar tag..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div
            className="rounded-md border border-gray-300 p-2 bg-gray-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1 text-sm overflow-y-auto items-start"
            style={{
              maxHeight: "500px",
              minHeight: "500px",
              gridAutoRows: "min-content",
            }}
          >
            {tags
              ?.filter((tag) =>
                tag.name.toLowerCase().includes(searchTerm.toLowerCase())
              )
              .map((tag) => (
                <label key={tag.id} className="flex items-center mb-2">
                  <Checkbox
                    type="checkbox"
                    className="mr-2"
                    checked={mandatoryTags.includes(tag.id)}
                    onChange={() => {
                      setMandatoryTags((prev) =>
                        prev.includes(tag.id)
                          ? prev.filter((id) => id !== tag.id)
                          : [...prev, tag.id]
                      );
                    }}
                  />
                  {tag.name}
                </label>
              ))}
          </div>
        </div>

        <Button
          type="submit"
          className="w-full primary"
          isProcessing={isSubmitting}
          processingSpinner={
            <AiOutlineLoading className="h-6 w-6 animate-spin" />
          }
          disabled={isSubmitting}
        >
          Atualizar configurações do laudo
        </Button>

        {alert.show && (
          <Alert className="mt-4" color={`${alert.type}`}>
            <span className="font-medium">{alert.message}</span>
          </Alert>
        )}
      </form>
    </div>
  );
};

export default SurveyConfig;
